import { makeAwsSignedRequest } from './ApiService';

export interface AiDiagnosisHistoryItem {
    generatedAt: string;
    response: string;
    /** PSTriage session (ORCA objectId) that produced this earlier diagnosis; absent for older entries. */
    agentSessionId?: string;
}

export interface AiDiagnosisEntry {
    aiAssistKey: string;
    diagnosticCode: string;
    diagnosticDescription: string;
    clients: string[];
    processors: string[];
    sampleRefundIds: string[];
    refundIds: string[];
    status: 'COMPLETE' | 'IN_PROGRESS' | 'ERROR';
    generatedAt?: string;
    response?: string;
    agentSessionId?: string;
    submittedAt?: string;
    history: AiDiagnosisHistoryItem[];
}

/** Stable client-side join key for a diagnostic bucket. Must mirror the row's identity. */
export const bucketKey = (diagnosticCode: string, diagnosticDescription: string): string =>
    `${diagnosticCode ?? ''}||${diagnosticDescription ?? ''}`;

/**
 * Fetches the PSTriage AI-diagnosis store and returns it indexed by bucket key
 * (diagnosticCode + description), so the diagnostic view can look up a row's diagnosis directly.
 * The backend pre-computes these during the data refresh; this is a plain read.
 */
export const fetchAiDiagnostics = async (
    stage: string,
    realm: string,
): Promise<Map<string, AiDiagnosisEntry>> => {
    const store = await makeAwsSignedRequest(stage, realm, 'ai-diagnostics');
    const byBucket = new Map<string, AiDiagnosisEntry>();
    if (store && typeof store === 'object') {
        for (const entry of Object.values(store) as AiDiagnosisEntry[]) {
            if (entry && entry.diagnosticCode !== undefined) {
                byBucket.set(bucketKey(entry.diagnosticCode, entry.diagnosticDescription), entry);
            }
        }
    }
    return byBucket;
};
